import React from 'react';
import getConfig from 'next/config';
import { useRouter } from 'next/router';
import { GetStaticPaths, GetStaticProps } from 'next';
import StickyBox from 'react-sticky-box';
import classNames from 'classnames';
import { createClient } from 'redis';
import { SiteHead } from '../../components/document/Head/Head';

import ArticlePosts from '../../components/ArticlePosts/ArticlePosts';
import SidebarLeadForm from '../../components/LeadForm/SidebarLeadForm/SidebarLeadForm';
import StickyWhatsapp from '../../components/StickyWhatsapp';

import useResponsive from '../../utils/hooks/useResponsive';
import useAuthenticated from '../../utils/hooks/useAuthenticated';
import { log_info, log_error } from '../../utils/logger';
import { formatUrl } from '../../lib/formatUrl';
import { REDIS_TTL, REDIS_TTL_ERROR } from '../../lib/constant';
import { getCategories, getCategory, getPosts } from '../../lib/graphqlQuery';
import useWebView from '../../utils/hooks/useWebView';

import { BottomNavigation, ViewMore } from '@lifepal/lifepal-design-system';
import {
  NORMAL_MENU,
  LOGIN_MENU,
  ACCOUNT_PROPS,
  INSURANCE_LINKS,
  TOOLS_PROPS,
  EXPLORE_PROPS,
} from '../../lib/bottomNavConstant';
import { VIEW_MORE_MENU } from '../../lib/viewMoreConstant';

import styles from '../../styles/pages/CategoryPage.module.scss';

const { REDIS_HOST, REDIS_URL } = getConfig().serverRuntimeConfig;

const CategoryPage = (props) => {
  const router = useRouter();
  const { isDesktop } = useResponsive();
  const authenticated = useAuthenticated();
  const isWebView = useWebView();

  const { posts, category } = props;
  const currentURL = formatUrl(router.basePath, router.asPath);

  const setStickyComponent = () => {
    if (!isWebView) {
      if (!isDesktop && authenticated) {
        <ViewMore
          menu={VIEW_MORE_MENU}
          insuranceLinks={INSURANCE_LINKS}
          toolsProps={TOOLS_PROPS}
          exploreProps={EXPLORE_PROPS}
        />
      }
      return <StickyWhatsapp />
    }
  }

  return (
    <>
      <SiteHead
        category={category}
        title={category.seo && category.seo.title ? category.seo.title : category.name}
        url={currentURL}
      />
      <div className={styles['category-page']}>
        <div className="row">
          <div className={classNames(styles['row__content'], 'col-lg-8 col-md-12')}>
            <h1 className={styles['category-page__title']}>Category: {category.name}</h1>
            <div
              className={styles['category-page__description']}
              dangerouslySetInnerHTML={{ __html: category.description }}
            />
            <ArticlePosts
              posts={posts}
              category={category.slug}
              inArticleBanners={category.inArticleBanners}
            />
          </div>
          <div className="col-lg-4 col-md-12">
            {isDesktop && (
              <StickyBox offsetTop={100} className={styles['tag-page__sideBar']}>
                <div>
                  <SidebarLeadForm category="media" />
                </div>
              </StickyBox>
            )}
          </div>
        </div>
      </div>
      {setStickyComponent()}

      {!isDesktop && !isWebView && authenticated && (
        <BottomNavigation
          activePath={router.asPath}
          isLoggedIn={true}
          normalMenu={NORMAL_MENU} //menu when user isn't logged in
          loginMenu={LOGIN_MENU}
          accountProps={ACCOUNT_PROPS} //props for Account section in normal menu
          insuranceLinks={INSURANCE_LINKS}
          toolsProps={TOOLS_PROPS}
          exploreProps={EXPLORE_PROPS}
        />
      )}
    </>
  );
};

export const getStaticProps: GetStaticProps = async (context) => {
  const { params } = context;

  const activeSlug = Array.isArray(params.slug) ? params.slug[params.slug.length - 1] : params.slug;

  const paths = Array.isArray(params.slug)
    ? `category/${params.slug.join('/')}/`
    : `category/${params.slug}/`;

  let data: any;

  if (typeof paths === 'string' && paths.indexOf(' ') >= 0) {
    return {
      redirect: {
        destination: '/',
        statusCode: 301,
      },
    };
  }

  if (REDIS_HOST) {
    const redisKey = `category:${activeSlug}`;

    const redis = createClient({ url: REDIS_URL });
    redis.on('error', (err) => log_error('Redis Createclient error on getCategory', 'error', err));

    await redis.connect();
    const value = await redis.get(redisKey);

    if (!value) {
      const { data: dataCategory } = await getCategory(activeSlug, paths);
      data = dataCategory;
      let redisTTL = REDIS_TTL;
      if (data.status && data.status !== 200) {
        redisTTL = REDIS_TTL_ERROR;
        log_error('getCategory', 'error', data);
      }
      await redis.setEx(redisKey, redisTTL, JSON.stringify(dataCategory));
      log_info('getCategory', 'response', `Key ${redisKey} stored in redis`);
    } else {
      data = JSON.parse(value);
      log_info('getCategory', 'response', `Key ${redisKey} fetched from redis`);
    }

    await redis.quit();
  } else {
    const { data: dataCategory } = await getCategory(activeSlug, paths);
    data = dataCategory;
    log_error('getCategory: Redis option', 'error', 'Redis is not available');
  }
  log_info('getCategory', 'response', data);

  if (data && data.redirect && data.redirect.urlTo) {
    const permanentStatus = data.redirect.status === '301';
    return {
      redirect: {
        destination: data.redirect.urlTo,
        permanent: permanentStatus,
      },
    };
  }

  log_info('getPosts', 'param', { category: activeSlug, first: 20 });
  const resPost = await getPosts({ category: activeSlug, first: 20 });
  log_info('getPosts', 'response', resPost);

  return {
    notFound: !data.category,
    props: {
      posts: resPost.data && resPost.data.posts ? resPost.data.posts : {},
      category: data.category,
    },
    revalidate: 2 * 60, // cache API response for 2 minutes
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  const { data } = await getCategories();
  log_info('getCategories', 'response', data);

  return {
    paths:
      data.categories && data.categories.edges
        ? data.categories.edges.map(({ node }) => `/category${node.uri}`)
        : [],
    fallback: 'blocking',
  };
};

export default CategoryPage;
